import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, 
  ExternalLink, 
  ArrowRight, 
  Plus,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RecentInterviewsProps {
  onNavigateToInterviews?: () => void;
  limit?: number;
  projectId?: string | null;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'scheduled':
      return <Clock className="h-4 w-4 text-blue-600" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-red-600" />;
    default:
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'scheduled':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'cancelled':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  }
}

export function RecentInterviews({ 
  onNavigateToInterviews, 
  limit = 5,
  projectId = null 
}: RecentInterviewsProps) {
  const navigate = useNavigate();

  // Mock data for now - in real app, this would come from a hook like useInterviews()
  const interviews = useMemo(() => [
    // This would be populated from actual interview data
    // For now, returning empty array since we don't have interview data loaded
  ], []);

  // Filter interviews by project if needed
  const filteredInterviews = useMemo(() => {
    if (projectId) {
      return interviews.filter(interview => 
        (interview as any).project_id === projectId || (interview as any).projectId === projectId
      );
    }
    return interviews;
  }, [interviews, projectId]);

  // Filter and sort recent interviews
  const recentInterviews = useMemo(() => {
    return filteredInterviews
      .sort((a, b) => new Date((b as any).created_at || (b as any).createdAt || 0).getTime() - new Date((a as any).created_at || (a as any).createdAt || 0).getTime())
      .slice(0, limit);
  }, [filteredInterviews, limit]);

  // Helper function to handle navigation
  const handleNavigateToInterviews = () => {
    if (onNavigateToInterviews) {
      onNavigateToInterviews();
    } else {
      navigate('/?tab=interview-management');
    }
  };

  // Calculate activity stats
  const activityStats = useMemo(() => {
    const totalInterviews = filteredInterviews.length;
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentInterviews = filteredInterviews.filter(interview => 
      new Date((interview as any).created_at || (interview as any).createdAt || 0) > oneWeekAgo
    ).length;

    return {
      totalInterviews,
      recentInterviews
    };
  }, [filteredInterviews]);

  // Loading State - simplified since we don't have actual loading
  const isLoading = false;

  if (isLoading) {
    return (
      <div className="space-y-4 pt-2 min-h-[200px]">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty State
  if (recentInterviews.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8 flex flex-col items-center min-h-[200px] justify-center space-y-4">
        <Users className="w-12 h-12 text-slate-400" />
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-600">
            No interviews scheduled yet
          </p>
          <p className="text-xs text-slate-500">
            Schedule and conduct interviews with candidates to streamline your hiring process.
          </p>
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleNavigateToInterviews}
          className="mt-4 border-slate-200 hover:bg-slate-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Schedule Interview
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Activity Statistics */}
      {activityStats.totalInterviews > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.recentInterviews}</div>
            <div className="text-xs text-slate-600 font-medium">This Week</div>
          </div>
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.totalInterviews}</div>
            <div className="text-xs text-slate-600 font-medium">Total</div>
          </div>
        </div>
      )}

      {/* Interview Items */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {recentInterviews.map((interview) => (
          <div 
            key={(interview as any).id} 
            className="flex items-center space-x-4 p-4 hover:bg-slate-50 rounded-xl transition-all duration-200 cursor-pointer group border border-transparent hover:border-slate-200"
            onClick={handleNavigateToInterviews}
          >
            <div className={cn(
              "w-12 h-12 flex items-center justify-center rounded-lg text-sm font-bold transition-all duration-200 border",
              getStatusColor((interview as any).status || 'scheduled')
            )}>
              {getStatusIcon((interview as any).status || 'scheduled')}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-slate-700 transition-colors">
                {(interview as any).candidateName || `${(interview as any).firstName} ${(interview as any).lastName}`}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {(interview as any).position || (interview as any).role}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatTimeAgo((interview as any).created_at || (interview as any).createdAt)}
                </span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </div>
        ))}
      </div>

      {/* View All Button */}
      {recentInterviews.length > 0 && (
        <div className="pt-4 border-t border-slate-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNavigateToInterviews}
            className="w-full justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
          >
            View All Interviews
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
} 